import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { authApi } from '@/api';
import { ORG_SIGN_IN_PATH } from '@/constants/authRoutes';
import { useMemo, useState } from 'react';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { AuthButton } from '@/core/auth/components/AuthButton';

type FormData = { newPassword: string; confirmPassword: string };

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [loading, setLoading] = useState(false);

  const schema = useMemo(
    () =>
      z
        .object({
          newPassword: z.string().min(8, t('resetPassword.passwordMin8')),
          confirmPassword: z.string(),
        })
        .refine((d) => d.newPassword === d.confirmPassword, {
          message: t('resetPassword.passwordsMismatch'),
          path: ['confirmPassword'],
        }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    if (!token) {
      notify.error(t('resetPassword.invalidLink'));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({ token, newPassword: data.newPassword });
      notify.t.success('resetPassword.success', { ns: 'auth' });
      navigate(ORG_SIGN_IN_PATH);
    } catch (err) {
      showApiError(err, t('resetPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title={t('resetPassword.title')}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <AuthInput
          label={t('resetPassword.newPassword')}
          type="password"
          autoComplete="new-password"
          {...register('newPassword')}
          error={errors.newPassword?.message}
        />
        <AuthInput
          label={t('resetPassword.confirmPassword')}
          type="password"
          autoComplete="new-password"
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
        <AuthButton type="submit" loading={loading} disabled={!token}>
          {t('resetPassword.submit')}
        </AuthButton>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <Link to={ORG_SIGN_IN_PATH} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
          {t('resetPassword.backToLogin')}
        </Link>
      </p>
    </AuthCard>
  );
}
